// Aggregate connection-health signals into one site-wide state, so a body bar
// can tell the member "we've lost the server" and auto-dismiss on recovery
// (plan item 4). Primary signal: the presence socket, which is always open when
// signed in and reconnects continuously — a healthy socket is the universal
// "server is reachable" proof. Secondary: api.ts fetch failures. Pure and
// synchronous (no timers, no DOM) so it unit-tests directly.

export type ConnState = "ok" | "degraded" | "down";

/** Escalate to `down` once the socket has retried at least this many times: one
 *  blip is `degraded` (not surfaced), sustained failure is `down`. With the
 *  presence socket's 0.5s→8s backoff, two misses is ~1.5s of real downtime. */
const DOWN_AFTER_RETRIES = 2;

let socketReconnects = 0; // consecutive presence-socket reconnect attempts
let apiFailing = false; // last api request was a network error / 5xx
let current: ConnState = "ok";
const listeners = new Set<(s: ConnState) => void>();

function recompute(): void {
  const next: ConnState =
    apiFailing || socketReconnects >= DOWN_AFTER_RETRIES
      ? "down"
      : socketReconnects >= 1
        ? "degraded"
        : "ok";
  if (next === current) return;
  current = next;
  for (const cb of listeners) cb(current);
}

/** The presence socket reports its transport state. `open` is the universal
 *  recovery signal: a healthy socket means the server is back, so it also clears
 *  any api-driven failure. */
export function reportSocket(status: "open" | "reconnecting"): void {
  if (status === "open") {
    socketReconnects = 0;
    apiFailing = false;
  } else {
    socketReconnects += 1;
  }
  recompute();
}

/** An api.ts request outcome: `false` on a network error / 5xx (server-side or
 *  unreachable), `true` on any response the server actually produced. */
export function reportApi(ok: boolean): void {
  apiFailing = !ok;
  recompute();
}

export function connState(): ConnState {
  return current;
}

export function onConnState(cb: (s: ConnState) => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

/** Test-only: reset all signals and subscribers to the initial state. */
export function __resetConnState(): void {
  socketReconnects = 0;
  apiFailing = false;
  current = "ok";
  listeners.clear();
}
